import {inject, Injectable} from '@angular/core';
import {marked} from 'marked';
import {TranslateHelperService} from './translate-helper.service';
import {logger} from './logger.service';

export interface MarkdownContent {
	html: string;
	metadata?: {
		title?: string;
		description?: string;
		keywords?: string;
		category?: string;
		date?: string;
		dateModified?: string;
		readTime?: number;
		imageUrl?: string;
	};
}

@Injectable({
	           providedIn: 'root'
           })
export class MarkdownContentService {
	private readonly translateService = inject(TranslateHelperService);
	private readonly cache = new Map<string, MarkdownContent>();

	/**
	 * Load markdown content for a blog article
	 * @param slug - The slug of the blog article
	 * @param lang - Optional language code (defaults to current language)
	 */
	async loadBlogContent(slug: string, lang?: string): Promise<MarkdownContent> {
		const currentLang = lang || this.translateService.currentLang || 'en';
		const cacheKey = `blog-${slug}-${currentLang}`;

		if (this.cache.has(cacheKey)) {
			return this.cache.get(cacheKey)!;
		}

		const url = `/assets/content/blog/${slug}.${currentLang}.md`;
		return this.loadMarkdown(url, cacheKey);
	}

	/**
	 * Load markdown content for a practice page
	 * @param slug - The slug of the practice
	 * @param lang - Optional language code (defaults to current language)
	 */
	async loadPracticeContent(slug: string, lang?: string): Promise<MarkdownContent> {
		const currentLang = lang || this.translateService.currentLang || 'en';
		const cacheKey = `practice-${slug}-${currentLang}`;

		if (this.cache.has(cacheKey)) {
			return this.cache.get(cacheKey)!;
		}

		const url = `/assets/content/practices/${slug}.${currentLang}.md`;
		return this.loadMarkdown(url, cacheKey);
	}

	/**
	 * Load and parse markdown file
	 * @param url - URL of the markdown file
	 * @param cacheKey - Key for caching
	 */
	private async loadMarkdown(url: string, cacheKey: string): Promise<MarkdownContent> {
		try {
			const markdown = await this.fetchText(url);
			const content = this.parseMarkdown(markdown);
			this.cache.set(cacheKey, content);
			return content;
		} catch (error) {
			logger.error(`Error loading markdown from ${url}:`, error);
			// Fallback to French if current language fails
			const fallbackUrl = url.replace(/\.(en|it|de|es)\.md$/, '.fr.md');
			if (fallbackUrl !== url) {
				try {
					const markdown = await this.fetchText(fallbackUrl);
					const content = this.parseMarkdown(markdown);
					this.cache.set(cacheKey, content);
					return content;
				} catch {
					return {
						html: '<p>Content not available in this language.</p>',
						metadata: {}
					};
				}
			}
			return {
				html: '<p>Content not available.</p>',
				metadata: {}
			};
		}
	}

	private async fetchText(url: string): Promise<string> {
		const response = await fetch(url, {credentials: 'same-origin'});
		if (!response.ok) {
			throw new Error(`Failed to load ${url}: ${response.status} ${response.statusText}`);
		}
		return response.text();
	}

	/**
	 * Parse markdown content and extract frontmatter metadata
	 */
	private parseMarkdown(markdown: string): MarkdownContent {
		// Parse frontmatter if present (support LF and CRLF)
		const frontmatterRegex = /^---\s*\r?\n([\s\S]*?)\r?\n---\s*\r?\n([\s\S]*)$/;
		const match = markdown.match(frontmatterRegex);

		let content = markdown;
		const metadata: NonNullable<MarkdownContent['metadata']> = {};

		if (match) {
			const frontmatter = match[1];
			content = match[2];

			// Parse YAML-like frontmatter
			frontmatter.split(/\r?\n/).forEach(line => {
				const colonIndex = line.indexOf(':');
				if (colonIndex > 0) {
					const rawKey = line.substring(0, colonIndex).trim();
					const keyLower = rawKey.toLowerCase();
					const value = line.substring(colonIndex + 1).trim().replace(/^["']|["']$/g, '');
					
					// Handle specific keys with special processing
					if (keyLower === 'readtime') {
						const numValue = parseInt(value, 10);
						metadata.readTime = isNaN(numValue) ? 0 : numValue;
					} else if (keyLower === 'imageurl') {
						metadata.imageUrl = value;
					} else if (keyLower === 'datemodified') {
						metadata.dateModified = value;
					} else {
						// Handle other keys
						const key = keyLower as keyof MarkdownContent['metadata'];
						metadata[key] = value as never;
					}
				}
			});
		}

		// Configure marked options (global, only needed once but safe to call multiple times)
		marked.setOptions({
			breaks: true,
			gfm: true
		});

		// Convert markdown to HTML
		const html = marked.parse(content) as string;

		// Ensure we have valid HTML
		if (!html || html.trim().length === 0) {
			logger.warn('Markdown parsing resulted in empty HTML');
			return {
				html: '<p>Contenu non disponible.</p>',
				metadata
			};
		}

		// Calculate reading time if not provided in metadata
		// Standard reading speed: 200 words per minute
		if (!metadata.readTime || metadata.readTime === 0) {
			const wordCount = content.split(/\s+/).filter(word => word.length > 0).length;
			metadata.readTime = Math.max(1, Math.ceil(wordCount / 200));
		}

		return {html, metadata};
	}

	/**
	 * Clear cache (useful when language changes)
	 */
	clearCache(): void {
		this.cache.clear();
	}

	/**
	 * Clear cache for a specific content type and slug
	 */
	clearCacheFor(slug: string, contentType: 'blog' | 'practice'): void {
		const prefix = contentType === 'blog' ? 'blog-' : 'practice-';
		const keysToDelete: string[] = [];
		this.cache.forEach((value, key) => {
			if (key.startsWith(`${prefix}${slug}-`)) {
				keysToDelete.push(key);
			}
		});
		keysToDelete.forEach(key => this.cache.delete(key));
	}
}

